// Reference:
// https://www.youtube.com/watch?v=yAI9fbbH-rM
// https://apexcharts.com/react-chart-demos/column-charts/stacked-columns-100/

import { useEffect, useState } from 'react'
import Chart from 'react-apexcharts'
import { useTheme } from '../../../../contexts/ThemeContext'

//number formatter to avoid 0.0000000000 + long float labels
const formatNumber = (val) => {
  const n = Number(val) || 0

  if (n === 0) return '0'

  //compact format for big values
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`

  //otherwise up to 2 decimals, trim trailing zeros
  return n.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')
}

const ApexColumnChart = ({
  data,
  title,
  height,
  onDataPointSelection, 
}) => {
  const { theme } = useTheme()
  const [chartData, setChartData] = useState()

  useEffect(() => {
    if (theme === 'dark') {
      renderChart('#d1d5db')
    } else {
      renderChart('#6b7280')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, data])

  function renderChart(labelColor) {
    setChartData({
      series: data || [],
      options: {
        chart: {
          fontFamily: 'DM Sans, sans-serif',
          stacked: true,
          toolbar: {
            show: false,
          },
          events: {
            //CLICK HANDLER
            dataPointSelection: (event, chartContext, config) => {
              if (!onDataPointSelection) return

              const seriesIndex = config.seriesIndex
              const dataPointIndex = config.dataPointIndex
              const seriesName = data?.[seriesIndex]?.name

              if (seriesName != null && dataPointIndex != null) {
                onDataPointSelection(seriesName, dataPointIndex)
              }
            },
          },
        },

        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '80%',
            endingShape: 'rounded',
            cursor: onDataPointSelection ? 'pointer' : 'default', 
          },
        },

        legend: {
          show: true,
          position: 'right',
          showForSingleSeries: true,
          horizontalAlign: 'center',
          fontSize: '14px',
          fontFamily: 'Inter',
          fontWeight: 400,
          labels: {
            colors: labelColor,
            useSeriesColors: true,
          },
        },

        dataLabels: {
          enabled: true,
          formatter: (val) => formatNumber(val), 
        },

        stroke: {
          show: true,
          width: 2,
          colors: ['transparent'],
        },

        colors: ['#F89C74', '#66C5CC', '#8BE0A4', '#DCB0F2', '#F6CF71'],

        xaxis: {
          title: {
            text: 'Months',
            style: {
              color: labelColor,
              fontWeight: '500',
              fontSize: '12px',
            },
          },
          categories: [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
          ],
          labels: {
            style: {
              colors: labelColor,
            },
          },
        },

        yaxis: {
          title: {
            text: 'Tonnes of CO2',
            style: {
              color: labelColor,
              fontWeight: '500',
              fontSize: '14px',
            },
          },
          labels: {
            style: {
              colors: labelColor,
            },
            formatter: (val) => formatNumber(val), 
          },
        },

        responsive: [
          {
            breakpoint: 480,
            options: {
              legend: {
                position: 'bottom',
                offsetX: -10,
                offsetY: 0,
              },
            },
          },
        ],

        fill: {
          opacity: 1,
        },

        tooltip: {
          y: {
            formatter: (val) => `${formatNumber(val)} tCO₂e`, 
          },
        },
      },
    })
  }

  return (
    <div className="px-2 py-2">
      <h3 className="text-left px-6">{title}</h3>

      {chartData && (
        <Chart
          options={chartData.options}
          series={chartData.series}
          type="bar"
          height={height}
        />
      )}
    </div>
  )
}

export default ApexColumnChart
